import React, { useEffect, useState, useRef, useMemo } from 'react'
import classNames from 'classnames'
import { Shimmer, Typography } from '@ct-design/lib'
import { SearchableDropdownProps, Item } from './type'
import { ChevronDown } from '../../assets/svg'
import styles from './searchableDropdown.module.css'
import useTheme from '@ct-design/hooks/useTheme'

// Default check icon component if none is provided
const DefaultCheckIcon = ({ stroke }: { stroke: string }) => (
    <svg
        width='16'
        height='16'
        viewBox='0 0 16 16'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
    >
        <path
            d='M13.3334 4L6.00008 11.3333L2.66675 8'
            stroke={stroke}
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
        />
    </svg>
)

// Default search filter function
const defaultSearchFilter = (items: Item[], searchTerm: string): Item[] => {
    if (!searchTerm.trim()) return items
    
    return items.filter(item => 
        item.item.toLowerCase().includes(searchTerm.toLowerCase())
    )
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
    placeHolder,
    itemList,
    disabled,
    hasError,
    dropDownValue,
    multiSelect,
    defaultSingleSelectedIndex,
    defaultMultiSelectedIndex,
    onItemSelect,
    customWrapperClass,
    id,
    dropDownItemListWrapperClass,
    showDropDownListOnTop,
    checkIcon: CheckIcon = DefaultCheckIcon,
    isLoading,
    searchPlaceholder = "Search...",
    enableSearch = true,
    searchFilterFunction = defaultSearchFilter,
    noResultsText = "No results found"
}) => {
    const [focus, setFocus] = useState<boolean>(false)
    const [isClosing, setIsClosing] = useState<boolean>(false)
    const [isHovering, setIsHovering] = useState<boolean>(false)
    const [singleSelectIndex, setSingleSelectIndex] = useState<number>()
    const [multiSelectIndex, setMultiSelectindex] = useState<number[]>()
    const [searchTerm, setSearchTerm] = useState<string>('')
    const dropdownRef = useRef<HTMLDivElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const theme = useTheme()

    // Filter items based on search term
    const filteredItems = useMemo(() => {
        if (!enableSearch) return itemList
        return searchFilterFunction(itemList, searchTerm)
    }, [itemList, searchTerm, enableSearch, searchFilterFunction])

    useEffect(() => {
        setMultiSelectindex(defaultMultiSelectedIndex)
        setSingleSelectIndex(defaultSingleSelectedIndex)
    }, [defaultMultiSelectedIndex, defaultSingleSelectedIndex])

    // Focus search input when dropdown opens
    useEffect(() => {
        if (focus && enableSearch && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus()
            }, 100)
        }
    }, [focus, enableSearch])

    // Add an effect to handle clicks outside the dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                focus &&
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node)
            ) {
                handleClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [focus])

    // Handle mouse enter/leave for dropdown list
    const handleMouseEnter = () => {
        setIsHovering(true)
    }

    const handleMouseLeave = () => {
        setIsHovering(false)
    }

    // Handle smooth dropdown closing
    const handleClose = () => {
        if (!focus || isClosing) return

        setIsClosing(true)
        if (dropdownRef.current) {
            dropdownRef.current.style.animation = `${styles.dropdownExit} 0.2s ease-out forwards`
            setTimeout(() => {
                setFocus(false)
                setIsClosing(false)
                setIsHovering(false)
                setSearchTerm('') // Clear search on close
            }, 200)
        } else {
            setFocus(false)
            setIsClosing(false)
            setIsHovering(false)
            setSearchTerm('') // Clear search on close
        }
    }

    const handleOpen = () => {
        if (focus || isClosing) return
        setFocus(true)
    }

    const toggleFocus = () => {
        if (focus) {
            handleClose()
        } else {
            handleOpen()
        }
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value)
    }

    const disabledClass =
        'w-100p px-3 select c-not-allowed bs-border bg-neutral-300 br-6 ba-solid flex flex-middle flex-between fs-14 lh-24 fw-500 c-neutral-700'

    const dropdownWrapperClass = classNames(
        'w-fit-content w-100p p-relative flex ba-solid br-6 bs-border bg-neutral-100',
        {
            'bc-primary-500': !hasError && focus,
            'bc-neutral-500': !hasError && !focus,
            'bc-error-500': hasError
        },
        customWrapperClass
    )

    const selectClass = classNames('o-0 w-100p px-3 p-absolute fs-16 lh-24', {
        select: dropDownValue.length <= 0,
        'py-2': dropDownValue.length !== 0
    })

    const labelClass = classNames(
        'w-100p p-absolute px-3 fs-14 lh-24 w-100p tt-ellipsis',
        {
            select: dropDownValue.length <= 0,
            'py-2': dropDownValue.length !== 0
        }
    )

    const chevronWrapperClass = 'h-100p flex flex-middle pl-1 pr-3'

    const chevronClass = classNames(styles.chevron, {
        [styles.active]: focus && !isClosing
    })

    const dropdownHeadingItemClass =
        'fs-12 lh-16 fw-600 c-neutral-700 mt-4 mb-2 px-4 tt-ellipsis'

    const dropdownItemClass =
        'flex flex-middle br-4 fs-14 lh-24 fw-400 c-neutral-900 c-pointer tt-ellipsis px-2 py-3'

    const singleSelectClickHandler = (item: Item, originalIndex: number) => {
        // Find the original index in the unfiltered list
        const actualIndex = itemList.findIndex(originalItem => originalItem.key === item.key)
        setSingleSelectIndex(actualIndex)
        onItemSelect(item)
        handleClose()
    }

    const multiSelectClickhandler = (item: Item, originalIndex: number) => {
        // Find the original index in the unfiltered list
        const actualIndex = itemList.findIndex(originalItem => originalItem.key === item.key)
        
        if (multiSelectIndex) {
            const removeIndex = multiSelectIndex.indexOf(actualIndex)
            if (removeIndex !== -1) {
                const multiSelectIndexState = [...multiSelectIndex]
                multiSelectIndexState.splice(removeIndex, 1)
                setMultiSelectindex([...multiSelectIndexState])
            } else {
                const multiSelectIndexState = [...multiSelectIndex]
                setMultiSelectindex([...multiSelectIndexState, actualIndex])
            }
        }
        onItemSelect(item)
    }

    if (isLoading) {
        return (
            <Shimmer
                borderRadius={theme.border.radius[6]}
                shimmerWrapperClass={dropdownWrapperClass}
                height={'40px'}
                width='100%'
            />
        )
    }

    if (disabled) {
        return (
            <div
                className={classNames(disabledClass, {
                    'bc-neutral-500': !hasError,
                    'bc-error-500': hasError
                })}
                style={{ height: 40 }}
            >
                <div className='flex flex-column o-hidden'>
                    <div
                        className={`${
                            dropDownValue.length <= 0
                                ? 'fs-14 lh-24'
                                : 'fs-12 lh-16'
                        } fw-500 c-neutral-700 tt-ellipsis`}
                    >
                        {placeHolder}
                    </div>
                    {dropDownValue.length > 0 && (
                        <div className='fs-14 lh-24 fw-500 c-neutral-900 tt-ellipsis'>
                            {dropDownValue}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    const dropDownItemList = (focus || isClosing) && (
        <div
            ref={dropdownRef}
            className={classNames(
                'p-absolute z-10 ba-solid bc-neutral-500 bg-neutral-100 br-4 p-1 l-0 r-0 mt-1 w-100p',
                styles.dropdown,
                dropDownItemListWrapperClass,
                { [styles.hovering]: isHovering }
            )}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={
                {
                    '--dropdown-hover-bg': theme.color.dropdown.hoverBg,
                    '--dropdown-hover-text': theme.color.dropdown.hoverText,
                    '--dropdown-selected-bg': theme.color.dropdown.selectedBg,
                    '--dropdown-selected-text': theme.color.dropdown.selectedText,
                    '--dropdown-item-color': theme.color.dropdown.itemColor,
                    '--dropdown-item-bg': theme.color.dropdown.itemBg
                } as React.CSSProperties
            }
        >
            {/* Search Input */}
            {enableSearch && (
                <div className={styles['search-wrapper']}>
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder={searchPlaceholder}
                        className={styles['search-input']}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}

            {/* Dropdown Items */}
            {filteredItems && filteredItems.length > 0 ? (
                filteredItems.map((item, index) => {
                    const { key, item: dropdownItem, isHeading } = item || {}
                    const originalIndex = itemList.findIndex(originalItem => originalItem.key === key)
                    
                    if (isHeading) {
                        return (
                            <Typography
                                variant='P1'
                                key={key}
                                className={`${dropdownHeadingItemClass}`}
                            >
                                {dropdownItem.toUpperCase()}
                            </Typography>
                        )
                    }
                    
                    if (multiSelect) {
                        return (
                            <div
                                key={key}
                                className={classNames(
                                    dropdownItemClass,
                                    styles['dropdown-item'],
                                    'flex flex-middle',
                                    {
                                        [styles.selected]: multiSelectIndex
                                            ? multiSelectIndex.includes(originalIndex)
                                            : false
                                    }
                                )}
                                onClick={() =>
                                    multiSelectClickhandler(item, originalIndex)
                                }
                            >
                                {multiSelectIndex &&
                                    multiSelectIndex.includes(originalIndex) && (
                                        <span className='mr-2 w-4 h-4 flex-no-shrink'>
                                            <CheckIcon
                                                stroke={
                                                    theme.color.dropdown.selectedText
                                                }
                                            />
                                        </span>
                                    )}
                                <div>{dropdownItem}</div>
                            </div>
                        )
                    }
                    
                    return (
                        <div
                            key={key}
                            className={classNames(
                                dropdownItemClass,
                                styles['dropdown-item'],
                                {
                                    [styles.selected]: originalIndex === singleSelectIndex
                                }
                            )}
                            onClick={() =>
                                singleSelectClickHandler(item, originalIndex)
                            }
                        >
                            <span className='mr-2 w-4 h-4 flex-no-shrink'>
                                {originalIndex === singleSelectIndex ? (
                                    <CheckIcon
                                        stroke={theme.color.dropdown.selectedText}
                                    />
                                ) : (
                                    ''
                                )}
                            </span>
                            {dropdownItem}
                        </div>
                    )
                })
            ) : (
                <div className={classNames(dropdownItemClass, styles['no-results'])}>
                    {enableSearch && searchTerm ? noResultsText : 'No items available'}
                </div>
            )}
        </div>
    )

    return (
        <div ref={wrapperRef} className='p-relative'>
            {showDropDownListOnTop && dropDownItemList}
            <div
                className={`${dropdownWrapperClass}`}
                style={{ height: 40 }}
                onClick={toggleFocus}
            >
                <div className='w-100p p-relative flex flex-middle'>
                    <select
                        className={`${selectClass}`}
                        style={{ display: 'none' }}
                    ></select>
                    <label
                        id={`${id}_label`}
                        htmlFor={id}
                        className={`${labelClass}`}
                    >
                        {dropDownValue.length > 0 ? (
                            <>
                                <div
                                    className='fs-14 lh-20 fw-400 c-neutral-900 tt-ellipsis'
                                    style={{
                                        color: theme.color.dropdown.itemColor
                                    }}
                                >
                                    {Array.isArray(dropDownValue)
                                        ? dropDownValue.join(', ')
                                        : dropDownValue}
                                </div>
                            </>
                        ) : (
                            <div
                                className='fs-14 lh-24 fw-400 tt-ellipsis'
                                style={{
                                    color: theme.color.dropdown.itemColor
                                }}
                            >
                                {placeHolder}
                            </div>
                        )}
                    </label>
                </div>
                <div
                    key={placeHolder}
                    id={`${id}_icon`}
                    className={`${chevronWrapperClass}`}
                    onClick={toggleFocus}
                >
                    <div className={`${chevronClass}`} style={{ height: 24 }}>
                        <ChevronDown />
                    </div>
                </div>
            </div>
            {!showDropDownListOnTop && dropDownItemList}
        </div>
    )
}

SearchableDropdown.defaultProps = {
    height: 52,
    hasError: false,
    disabled: false,
    multiSelect: false,
    defaultSingleSelectedIndex: -1,
    defaultMultiSelectedIndex: [],
    dropDownValue: '',
    showDropDownListOnTop: false,
    searchPlaceholder: 'Search...',
    enableSearch: true,
    noResultsText: 'No results found'
}

export default SearchableDropdown